import React, { ReactElement, useEffect, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { TenantSettingsThemeParamTypes } from './themeRoute';
import {
  Button,
  Checkbox,
  Divider,
  FormContainer,
  FormItem,
  Input,
  Layout,
  Modal,
  NativeSelect,
  Row,
} from 'elmo-elements';
import SettingsHeader from '@/pages/Settings/components/Header';
import CKFileUpload from '@/components/CKFileUpload/CKFileUpload';
import ColourPicker from '@/components/Form/ColourPicker';
import { LayoutSectionComponent } from '@/components/LayoutSection';
import { CkFinderResourceTypeEnum } from '@/modules/types/ckFinder';
import { useForm } from 'react-hook-form';
import {
  useGetCurrentTenantProfileQuery,
  useGetThemeQuery,
  useUpdateThemeMutation,
} from '@/modules/services';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ThemeDataType, ThemeFormDataType } from '@/modules/types/theme';
import SettingsBaseRoutes, {
  TenantSettingsTabs,
} from '@/pages/Settings/SettingRoutes';
import { useUserHasRole } from '@/lib/auth/useUserHasRoles';
import { Role } from '@/lib/auth/roles/role.enum';
import MyLibraryRoutes from '../MyLibrary/MyLibraryRoutes';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';
import { getCkFileType, getCkFinderConfig } from '@/lib/util';

const iconSetData = ['White', 'Dark'];
const styleData = ['Normal', 'Bold'];
const fontData = [
  'Open Sans',
  'Helvetica',
  'Arial',
  'Comic Sans MS',
  'Courier New',
  'Georgia',
  'Lucida Sans Unicode',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
  'Custom Font',
  'Poppins',
  'Aleo',
];

const sizePixelData = [
  ...Array.from(Array(15).keys()).map((item) => `${item + 6}px`),
  '24px',
  '26px',
  '30px',
  '36px',
];

const sizeEmData = [
  '1em',
  '1.1em',
  '1.2em',
  '1.3em',
  '1.4em',
  '1.5em',
  '1.6em',
  '1.7em',
  '1.8em',
  '1.9em',
  '2em',
  '2.1em',
  '3.1em',
];
const fontSizeData = sizePixelData.concat(sizeEmData);

export const defaultThemeValue = {
  name: 'Default Theme',
  banner: '',
  bgImg: '',
  isDefault: false,
  headingMetadata: {
    heading1Font: 'Open Sans, sans-serif',
    heading1Size: '1.6em',
    heading1Color: 'ffffff',
    heading1Weight: 'normal',
    heading2Font: 'Open Sans, sans-serif',
    heading2Size: '1.4em',
    heading2Color: 'ffffff',
    heading2Weight: 'normal',
  },
  bodyMetadata: {
    bodyFont: 'Open Sans, sans-serif',
    bodySize: '1.1em',
    bodyColor: '000000',
    bodyFontWeight: 'normal',
  },
  menuMetadata: {
    menuColor1: '418151',
    menuColor2: '000000',
    menuIconSet: 0,
    isGradientMenu: 1,
    buttonColor: 'ffffff',
  },
  windowMetadata: {
    width: 1024,
    height: 670,
    windowBgColor: 'eaeaea',
  },
};

const schema = yup.object().shape({
  name: yup.string().max(50),
  banner: yup.string().optional().max(50),
  bgImg: yup.string().optional().max(50),
  isDefault: yup.boolean(),
  headingMetadata: yup.object().shape({
    heading1Font: yup.string().max(50),
    heading1Size: yup.string().max(50),
    heading1Color: yup
      .string()
      .max(7, 'Need to be valid Hexidecimal')
      .matches(
        /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
        'Need to be valid Hexidecimal',
      ),
    heading1Weight: yup.string().max(50),
    heading2Font: yup.string().max(50),
    heading2Size: yup.string().max(50),
    heading2Color: yup
      .string()
      .max(7, 'Need to be valid Hexidecimal')
      .matches(
        /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
        'Need to be valid Hexidecimal',
      ),
    heading2Weight: yup.string().max(50),
  }),
  bodyMetadata: yup.object().shape({
    bodyFont: yup.string().max(50),
    bodySize: yup.string().max(50),
    bodyColor: yup
      .string()
      .max(7, 'Need to be valid Hexidecimal')
      .matches(
        /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
        'Need to be valid Hexidecimal',
      ),
    bodyFontWeight: yup.string().max(50),
  }),
  windowMetadata: yup.object().shape({
    width: yup.number().min(1, 'Must be a positive number'),
    height: yup.number().min(1, 'Must be a positive number'),
    windowBgColor: yup
      .string()
      .max(7, 'Need to be valid Hexidecimal')
      .matches(
        /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
        'Need to be valid Hexidecimal',
      ),
  }),
  menuMetadata: yup.object().shape({
    menuColor1: yup
      .string()
      .max(7, 'Need to be valid Hexidecimal')
      .matches(
        /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
        'Need to be valid Hexidecimal',
      ),
    menuColor2: yup
      .string()
      .max(7, 'Need to be valid Hexidecimal')
      .matches(
        /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
        'Need to be valid Hexidecimal',
      ),
    menuIconSet: yup.number(),
    isGradientMenu: yup.number(),
    buttonColor: yup
      .string()
      .max(7, 'Need to be valid Hexidecimal')
      .matches(
        /(^#?([0-9a-f]{3}|[0-9a-f]{6})$)|(^$)/i,
        'Need to be valid Hexidecimal',
      ),
  }),
});

const ThemeSetupComponent = (): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { themeId } = useParams<TenantSettingsThemeParamTypes>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { data: themeData, isLoading, isSuccess } = useGetThemeQuery(themeId);

  const tenant = useGetCurrentTenantProfileQuery();
  const [updateTheme, updateThemeStatus] = useUpdateThemeMutation();
  const hasAdminRole = useUserHasRole([
    Role.Admin,
    Role.Super,
    Role.Migration,
    Role.CAdmin,
  ]);

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ThemeFormDataType>({
    resolver: yupResolver(schema),
    defaultValues: defaultThemeValue,
  });

  const handleChange = (key: keyof ThemeFormDataType, value: any) => {
    setValue(key, value);
  };

  const handleColorChange = (key: keyof ThemeFormDataType, value: string) => {
    setValue(key, value.replace('#', ''));
  };
  const handleCancelEdit = () => {
    history.push(
      generatePath(SettingsBaseRoutes.path, {
        tabSelected: TenantSettingsTabs.Theme,
      }),
    );
  };

  useEffect(() => {
    const hasThemeData = !isLoading && themeData;

    if (!hasThemeData) return;

    const themeFormData = { ...themeData };
    themeFormData.bgImg = themeFormData.bgImg ?? '';
    // Prepopulate form with themeData
    reset(themeFormData);
  }, [isLoading, themeData, reset]);

  useEffect(() => {
    // Redirect back to theme list - themeId is not valid
    if (isSuccess && !themeData) {
      history.replace(
        generatePath(SettingsBaseRoutes.path, {
          tabSelected: TenantSettingsTabs.Theme,
        }),
      );
    }
  }, [history, isSuccess, themeData]);

  useEffect(() => {
    if (!updateThemeStatus.isError && !updateThemeStatus.isSuccess) return;

    let message = 'Update theme error! Something went wrong.';
    if (updateThemeStatus.isSuccess) {
      message = 'Update theme successful.';
    }

    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });
  }, [updateThemeStatus]);

  const headerTitle = 'Edit theme';
  const backButton = {
    title: headerTitle,
    onClick: () => {
      history.goBack();
    },
  };

  if (hasAdminRole !== undefined && !hasAdminRole) {
    history.replace(generatePath(MyLibraryRoutes.path));
  }

  if (isLoading || !themeData) {
    return (
      <>
        <Layout.Header>
          <SettingsHeader headerTitle={headerTitle} backButton={backButton} />
        </Layout.Header>
        <div className="editor-layout__body">
          <Layout.Content>
            <div />
          </Layout.Content>
        </div>
      </>
    );
  }

  return (
    <>
      <Modal
        id="ThemeModalDialog"
        type="medium"
        isOpened={isModalOpen}
        closeModal={() => {
          // setDeleteThemeId('');
          setIsModalOpen(false);
        }}
        primaryButton={
          <>
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={() => handleCancelEdit()}>
              Continue
            </Button>
          </>
        }
        title="Cancel without saving"
      >
        All unsaved changes will be lost. Are you sure you want to leave this
        page?
      </Modal>
      <Layout.Header>
        <SettingsHeader headerTitle={headerTitle} backButton={backButton} />
      </Layout.Header>
      <div className="editor-layout__body">
        <Layout.Content>
          <form
            onSubmit={handleSubmit((submitData) => {
              const updateThemeData = {
                ...themeData,
                ...submitData,
              } as ThemeDataType;

              updateTheme(updateThemeData);
            })}
          >
            <LayoutSectionComponent
              className="remove-overflow-hidden"
              heading="Setup"
              isFirst={true}
              content={
                <>
                  <FormContainer maxColumns={2}>
                    <FormItem
                      label="Theme Name"
                      message={errors.name && errors.name?.message}
                      status={errors.name ? 'error' : 'default'}
                      isBlock={true}
                    >
                      <Input
                        {...register('name', {})}
                        onChange={(e) =>
                          handleChange(
                            e.target.name as keyof ThemeFormDataType,
                            e.target.value,
                          )
                        }
                        name="name"
                        value={watch('name')}
                        ariaLabel="name"
                      />
                    </FormItem>
                  </FormContainer>

                  <FormContainer maxColumns={2}>
                    <FormItem
                      label="Width"
                      message={errors.windowMetadata?.width?.message}
                      status={
                        errors.windowMetadata?.width ? 'error' : 'default'
                      }
                      size="sm"
                    >
                      <Input
                        {...register('windowMetadata.width', {})}
                        onChange={(e) =>
                          handleChange(
                            e.target.name as keyof ThemeFormDataType,
                            e.target.value,
                          )
                        }
                        htmlType="number"
                        value={watch('windowMetadata.width')}
                        name="windowMetadata.width"
                        ariaLabel="theme width"
                        inputAdornmentEnd="px"
                      />
                    </FormItem>

                    <FormItem
                      message={errors.windowMetadata?.height?.message}
                      status={
                        errors.windowMetadata?.height ? 'error' : 'default'
                      }
                      size="sm"
                      label="Height"
                    >
                      <Input
                        {...register('windowMetadata.height', {})}
                        onChange={(e) =>
                          handleChange(
                            e.target.name as keyof ThemeFormDataType,
                            e.target.value,
                          )
                        }
                        htmlType="number"
                        value={watch('windowMetadata.height')}
                        name="windowMetadata.height"
                        ariaLabel="theme height"
                        inputAdornmentEnd="px"
                      />
                    </FormItem>
                  </FormContainer>

                  <FormContainer>
                    <FormItem
                      label="Background Colour"
                      message={errors.windowMetadata?.windowBgColor?.message}
                      status={
                        errors.windowMetadata?.windowBgColor
                          ? 'error'
                          : 'default'
                      }
                      size="sm"
                    >
                      <ColourPicker
                        defaultColor={watch('windowMetadata.windowBgColor')}
                        handleChange={(e) =>
                          handleColorChange(
                            'windowMetadata.windowBgColor' as keyof ThemeFormDataType,
                            e,
                          )
                        }
                        register={register}
                        registerName="windowMetadata.windowBgColor"
                        isFloat={true}
                        watch={watch}
                      />
                    </FormItem>
                  </FormContainer>
                  <FormContainer>
                    <FormItem message="This theme will be automatically applied to new courses.">
                      <Checkbox
                        onChange={(e: any) => {
                          // We only allow to set but not unset default theme - unsetting will be done once a different default theme is selected.
                          setValue('isDefault', true);
                        }}
                        isChecked={watch('isDefault')}
                        name="default theme"
                        id="default theme"
                        label="Default theme"
                        ariaLabel="default theme checkbox"
                      />
                    </FormItem>
                  </FormContainer>
                </>
              }
            />

            <LayoutSectionComponent
              className="remove-overflow-hidden"
              heading="Typography"
              content={
                <>
                  <div>
                    <h4>Heading 1</h4>
                    <FormContainer maxColumns={2}>
                      <FormItem
                        label="Font"
                        size="sm"
                        message={errors.headingMetadata?.heading1Font?.message}
                        status={
                          errors.headingMetadata?.heading1Font
                            ? 'error'
                            : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('headingMetadata.heading1Font')}
                          onChange={(e) => {
                            handleChange(
                              'headingMetadata.heading1Font' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {fontData.map((font: string, index: number) => (
                            <NativeSelect.Option
                              label={font}
                              value={font}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                      <FormItem
                        label="Style"
                        size="sm"
                        message={
                          errors.headingMetadata?.heading1Weight?.message
                        }
                        status={
                          errors.headingMetadata?.heading1Weight
                            ? 'error'
                            : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('headingMetadata.heading1Weight')}
                          onChange={(e) => {
                            handleChange(
                              'headingMetadata.heading1Weight' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {styleData.map((style, index) => (
                            <NativeSelect.Option
                              label={style}
                              value={style}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                    </FormContainer>
                    <FormContainer maxColumns={2}>
                      <FormItem
                        label="Size"
                        size="sm"
                        message={errors.headingMetadata?.heading1Size?.message}
                        status={
                          errors.headingMetadata?.heading1Size
                            ? 'error'
                            : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('headingMetadata.heading1Size')}
                          onChange={(e) => {
                            handleChange(
                              'headingMetadata.heading1Size' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {fontSizeData.map((size: string, index: number) => (
                            <NativeSelect.Option
                              label={size}
                              value={size}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                      <FormItem
                        label="Colour"
                        size="sm"
                        message={errors.headingMetadata?.heading1Color?.message}
                        status={
                          errors.headingMetadata?.heading1Color
                            ? 'error'
                            : 'default'
                        }
                      >
                        <ColourPicker
                          defaultColor={watch('headingMetadata.heading1Color')}
                          handleChange={(e) => {
                            handleColorChange(
                              'headingMetadata.heading1Color' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                          register={register}
                          registerName="headingMetadata.heading1Color"
                          isFloat={true}
                          watch={watch}
                        />
                      </FormItem>
                    </FormContainer>
                  </div>
                  <div>
                    <h4>Heading 2</h4>
                    <FormContainer maxColumns={2}>
                      <FormItem
                        label="Font"
                        size="sm"
                        message={errors.headingMetadata?.heading2Font?.message}
                        status={
                          errors.headingMetadata?.heading2Font
                            ? 'error'
                            : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('headingMetadata.heading2Font')}
                          onChange={(e) => {
                            handleChange(
                              'headingMetadata.heading2Font' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {fontData.map((font: string, index: number) => (
                            <NativeSelect.Option
                              label={font}
                              value={font}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                      <FormItem
                        label="Style"
                        size="sm"
                        message={
                          errors.headingMetadata?.heading2Weight?.message
                        }
                        status={
                          errors.headingMetadata?.heading2Weight
                            ? 'error'
                            : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('headingMetadata.heading2Weight')}
                          onChange={(e) => {
                            handleChange(
                              'headingMetadata.heading2Weight' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {styleData.map((style, index) => (
                            <NativeSelect.Option
                              label={style}
                              value={style}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                    </FormContainer>
                    <FormContainer maxColumns={2}>
                      <FormItem
                        label="Size"
                        size="sm"
                        message={errors.headingMetadata?.heading2Size?.message}
                        status={
                          errors.headingMetadata?.heading2Size
                            ? 'error'
                            : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('headingMetadata.heading2Size')}
                          onChange={(e) => {
                            handleChange(
                              'headingMetadata.heading2Size' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {fontSizeData.map((size: string, index: number) => (
                            <NativeSelect.Option
                              label={size}
                              value={size}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                      <FormItem
                        label="Colour"
                        size="sm"
                        message={errors.headingMetadata?.heading2Color?.message}
                        status={
                          errors.headingMetadata?.heading2Color
                            ? 'error'
                            : 'default'
                        }
                      >
                        <ColourPicker
                          defaultColor={watch('headingMetadata.heading2Color')}
                          handleChange={(e) =>
                            handleColorChange(
                              'headingMetadata.heading2Color' as keyof ThemeFormDataType,
                              e,
                            )
                          }
                          register={register}
                          registerName="headingMetadata.heading2Color"
                          isFloat={true}
                          watch={watch}
                        />
                      </FormItem>
                    </FormContainer>
                  </div>
                  <div>
                    <h4>Body</h4>
                    <FormContainer maxColumns={2}>
                      <FormItem
                        label="Font"
                        size="sm"
                        message={errors.bodyMetadata?.bodyFont?.message}
                        status={
                          errors.bodyMetadata?.bodyFont ? 'error' : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('bodyMetadata.bodyFont')}
                          onChange={(e) => {
                            handleChange(
                              'bodyMetadata.bodyFont' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {fontData.map((font: string, index: number) => (
                            <NativeSelect.Option
                              label={font}
                              value={font}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                      <FormItem
                        label="Style"
                        size="sm"
                        message={errors.bodyMetadata?.bodyFontWeight?.message}
                        status={
                          errors.bodyMetadata?.bodyFontWeight
                            ? 'error'
                            : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('bodyMetadata.bodyFontWeight')}
                          onChange={(e) => {
                            handleChange(
                              'bodyMetadata.bodyFontWeight' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {styleData.map((style, index) => (
                            <NativeSelect.Option
                              label={style}
                              value={style}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                    </FormContainer>
                    <FormContainer maxColumns={2}>
                      <FormItem
                        label="Size"
                        size="sm"
                        message={errors.bodyMetadata?.bodySize?.message}
                        status={
                          errors.bodyMetadata?.bodySize ? 'error' : 'default'
                        }
                      >
                        <NativeSelect
                          value={watch('bodyMetadata.bodySize')}
                          onChange={(e) => {
                            handleChange(
                              'bodyMetadata.bodySize' as keyof ThemeFormDataType,
                              e,
                            );
                          }}
                        >
                          {fontSizeData.map((size: string, index: number) => (
                            <NativeSelect.Option
                              label={size}
                              value={size}
                              key={index}
                            />
                          ))}
                        </NativeSelect>
                      </FormItem>
                      <FormItem
                        label="Colour"
                        size="sm"
                        message={errors.bodyMetadata?.bodyColor?.message}
                        status={
                          errors.bodyMetadata?.bodyColor ? 'error' : 'default'
                        }
                      >
                        <ColourPicker
                          defaultColor={watch('bodyMetadata.bodyColor')}
                          handleChange={(e) =>
                            handleColorChange(
                              'bodyMetadata.bodyColor' as keyof ThemeFormDataType,
                              e,
                            )
                          }
                          register={register}
                          registerName="bodyMetadata.bodyColor"
                          isFloat={true}
                          watch={watch}
                        />
                      </FormItem>
                    </FormContainer>
                  </div>
                </>
              }
            />

            <LayoutSectionComponent
              className="remove-overflow-hidden"
              heading="Control menu"
              content={
                <FormContainer maxColumns={2}>
                  <FormItem
                    label="Primary colour"
                    size="sm"
                    message={errors.menuMetadata?.menuColor1?.message}
                    status={
                      errors.menuMetadata?.menuColor1 ? 'error' : 'default'
                    }
                  >
                    <ColourPicker
                      defaultColor={watch('menuMetadata.menuColor1')}
                      handleChange={(e) =>
                        handleColorChange(
                          'menuMetadata.menuColor1' as keyof ThemeFormDataType,
                          e,
                        )
                      }
                      register={register}
                      registerName="menuMetadata.menuColor1"
                      isFloat={true}
                      watch={watch}
                    />
                  </FormItem>
                  <FormItem
                    label="Icon set"
                    size="sm"
                    message={errors.menuMetadata?.menuIconSet?.message}
                    status={
                      errors.menuMetadata?.menuIconSet ? 'error' : 'default'
                    }
                  >
                    <NativeSelect
                      value={watch('menuMetadata.menuIconSet')}
                      onChange={(e) =>
                        handleChange(
                          'menuMetadata.menuIconSet' as keyof ThemeFormDataType,
                          e,
                        )
                      }
                    >
                      {iconSetData.map((size: string, index: number) => (
                        <NativeSelect.Option
                          label={size}
                          // TODO: confirm key value pair
                          value={index}
                          key={index}
                        />
                      ))}
                    </NativeSelect>
                  </FormItem>
                  <FormItem
                    label="Button text colour"
                    size="sm"
                    message={errors.menuMetadata?.buttonColor?.message}
                    status={
                      errors.menuMetadata?.buttonColor ? 'error' : 'default'
                    }
                  >
                    <ColourPicker
                      defaultColor={watch('menuMetadata.buttonColor')}
                      handleChange={(e) =>
                        handleColorChange(
                          'menuMetadata.buttonColor' as keyof ThemeFormDataType,
                          e,
                        )
                      }
                      register={register}
                      registerName="menuMetadata.buttonColor"
                      isFloat={true}
                      watch={watch}
                    />
                  </FormItem>
                </FormContainer>
              }
            />

            <LayoutSectionComponent
              className="mb-3 remove-overflow-hidden"
              heading="Assets"
              content={
                <FormContainer maxColumns={2}>
                  <FormItem
                    label="Banner"
                    isBlock={true}
                    message={
                      errors.banner?.message
                        ? errors.banner?.message
                        : 'Must be 1024px by 90px in size.'
                    }
                    status={errors.banner?.message ? 'error' : 'default'}
                  >
                    <CKFileUpload
                      connectorDetails={getCkFinderConfig({
                        type: getCkFileType([CkFinderResourceTypeEnum.IMAGES]),
                        tenantId: `${tenant.data?.tenantId}`,
                        entityId: `${themeData?.id}`,
                        folderName: 'themes',
                      })}
                      defaultFileName={watch('banner') || ''}
                      fileType="images"
                      handlerFunction={{
                        onFileChoose: (fileName) =>
                          handleChange(
                            'banner' as keyof ThemeFormDataType,
                            fileName,
                          ),
                      }}
                      register={register}
                      registerName="banner"
                    />
                  </FormItem>
                  <FormItem
                    label="Background image"
                    isBlock={true}
                    message={
                      errors.bgImg?.message
                        ? errors.bgImg?.message
                        : 'Must be 1024px by 500px in size.'
                    }
                    status={errors.bgImg?.message ? 'error' : 'default'}
                  >
                    <CKFileUpload
                      connectorDetails={getCkFinderConfig({
                        type: getCkFileType([CkFinderResourceTypeEnum.IMAGES]),
                        tenantId: `${tenant.data?.tenantId}`,
                        entityId: `${themeData?.id}`,
                        folderName: 'themes',
                      })}
                      defaultFileName={watch('bgImg') || ''}
                      fileType="images"
                      handlerFunction={{
                        onFileChoose: (fileName) =>
                          handleChange(
                            'bgImg' as keyof ThemeFormDataType,
                            fileName,
                          ),
                      }}
                      register={register}
                      registerName="bgImg"
                    />
                  </FormItem>
                </FormContainer>
              }
            />

            <Divider />

            <Row className="mt-5">
              <Button
                type="primary"
                htmlType="submit"
                ariaLabel="Submit Form"
                isUppercase={false}
                className="px-5"
              >
                Save
              </Button>
              <Button
                ariaLabel="preview theme"
                isUppercase={false}
                className="px-5"
                onClick={() =>
                  window.open(
                    `/preview-theme/${themeId}`,
                    'Theme preview',
                    `height=${themeData.windowMetadata.height + 1},width=${
                      themeData.windowMetadata.width + 1
                    }`,
                  )
                }
              >
                Preview
              </Button>
              <Button
                htmlType="reset"
                ariaLabel="cancel edit"
                isUppercase={false}
                className="px-5"
                onClick={() => setIsModalOpen(true)}
              >
                Cancel
              </Button>
            </Row>
          </form>
        </Layout.Content>
      </div>
    </>
  );
};
export default ThemeSetupComponent;
